// frontend/src/components/driver-availability/PunchInDialog.jsx
//
// Modal for capturing expected end time and (optional) preferred route.
// Reused for both punch-in and end-time extension.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedButton, ThemedInput, ThemedSelect } from '../themed';

// 8-hour default shift; safely above the 15-minute server-side minimum.
const DEFAULT_SHIFT_HOURS = 8;

function pad(n) { return String(n).padStart(2, '0'); }

// "YYYY-MM-DDTHH:mm" string in the user's local time (suitable for <input type="datetime-local">).
function toLocalInputValue(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const PunchInDialog = ({
    mode,
    entityId,
    initialRouteId,
    initialExpectedEndAt,
    submitting,
    onCancel,
    onSubmit
}) => {
    const { t } = useTranslation();
    const { background, text, cn } = useTheme();
    const [routes, setRoutes] = useState([]);
    const [routeId, setRouteId] = useState(initialRouteId ?? '');
    const [expectedLocal, setExpectedLocal] = useState(() => {
        if (initialExpectedEndAt) {
            return toLocalInputValue(new Date(initialExpectedEndAt));
        }
        const d = new Date(Date.now() + DEFAULT_SHIFT_HOURS * 3600 * 1000);
        return toLocalInputValue(d);
    });
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`/api/routes/${entityId}`);
                if (!cancelled) {
                    setRoutes(res.data?.data || res.data || []);
                }
            } catch (err) {
                console.warn('Failed to load routes for PunchInDialog', err);
            }
        })();
        return () => { cancelled = true; };
    }, [entityId]);

    const [minLocal] = useState(() => {
        const d = new Date(Date.now() + 15 * 60 * 1000);
        return toLocalInputValue(d);
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!expectedLocal) {
            setError(t('driverAvailability.minEndTimeError'));
            return;
        }
        const expectedDate = new Date(expectedLocal);
        if (expectedDate.getTime() < Date.now() + 15 * 60 * 1000) {
            setError(t('driverAvailability.minEndTimeError'));
            return;
        }
        setError(null);
        onSubmit({
            expectedEndAt: expectedDate.toISOString(),
            routeId: routeId === '' ? null : Number.parseInt(routeId, 10)
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className={cn('w-full max-w-md rounded-lg shadow-xl', background.elevated || background.primary)}>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <h2 className={cn('text-lg font-semibold', text.primary)}>
                        {mode === 'punch_in'
                            ? t('driverAvailability.punchIn')
                            : t('driverAvailability.extendEndTime')}
                    </h2>

                    <ThemedInput
                        type="datetime-local"
                        label={t('driverAvailability.expectedEndTimeLabel')}
                        value={expectedLocal}
                        min={minLocal}
                        onChange={(e) => setExpectedLocal(e.target.value)}
                        required
                    />

                    <div>
                        <label className={cn('block text-sm font-medium mb-1', text.secondary)}>
                            {t('driverAvailability.preferredRouteLabel')}
                        </label>
                        <ThemedSelect
                            value={routeId}
                            onChange={(e) => setRouteId(e.target.value)}
                        >
                            <option value="">{t('driverAvailability.noPreferredRoute')}</option>
                            {routes.map(r => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                        </ThemedSelect>
                    </div>

                    {error && <div className="text-sm text-red-600">{error}</div>}

                    <div className="flex justify-end gap-2 pt-2">
                        <ThemedButton type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
                            {t('cancel')}
                        </ThemedButton>
                        <ThemedButton type="submit" variant="primary" disabled={submitting}>
                            {mode === 'punch_in'
                                ? t('driverAvailability.punchIn')
                                : t('driverAvailability.save')}
                        </ThemedButton>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PunchInDialog;
